// ============================================================
// Add & Edit Fee Structure Page
// ============================================================

import { useState, useEffect } from 'react';
import { Box, Button, TextField, MenuItem, FormControl, InputLabel, Select, FormControlLabel, Switch, Paper, Typography, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useCreateFeeStructure, useUpdateFeeStructure } from '@/hooks/useFeeStructure';
import { useSchool } from '@/hooks/useSchool';
import { feeService } from '@/services/feeService';
import { academicSessionsApi } from '@/api/academicSessions.api';
import { studentsApi } from '@/api/students.api';
import { PATHS } from '@/router/paths';
import toast from 'react-hot-toast';

export function AddFeeStructurePage() {
  const { id } = useParams();
  const isEditMode = !!id;

  usePageMeta(isEditMode ? 'Edit Fee Structure' : 'Create Fee Structure', [
    { label: 'Fees', path: PATHS.FEES },
    { label: 'Structure', path: PATHS.FEES_STRUCTURE },
    { label: isEditMode ? 'Edit' : 'Create' },
  ]);

  const navigate = useNavigate();
  const { schoolId } = useSchool();
  const { mutate: createFeeStructure, isPending: isCreating } = useCreateFeeStructure();
  const { mutate: updateFeeStructure, isPending: isUpdating } = useUpdateFeeStructure();

  const isPending = isCreating || isUpdating;

  // Dropdown states
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    academic_year_id: '',
    class_id: '',
    section_id: '',
    is_default: true,
    is_active: true,
  });

  // Load Sessions and Classes/Sections
  useEffect(() => {
    if (schoolId) {
      // 1. Fetch Academic Sessions
      academicSessionsApi
        .getAllBySchool(schoolId)
        .then((res) => {
          const list = Array.isArray(res) ? res : res?.data || [];
          setSessions(list);
          if (!isEditMode) {
            const current = list.find((s) => s.is_current) || list[0];
            if (current?.id) {
              setFormData((prev) => ({ ...prev, academic_year_id: current.id }));
            }
          }
        })
        .catch((err) => console.error('Failed to load academic sessions:', err));

      // 2. Fetch Classes and Sections
      studentsApi
        .getClassesSections()
        .then((res) => {
          const list = res?.data || (Array.isArray(res) ? res : []);
          setClasses(list);
        })
        .catch((err) => console.error('Failed to load classes & sections:', err));
    }
  }, [schoolId, isEditMode]);

  // Load existing fee structure details when in Edit Mode
  useEffect(() => {
    if (schoolId && isEditMode && id) {
      setIsLoadingDetails(true);
      feeService
        .getFeeStructureById(schoolId, id)
        .then((res) => {
          const data = res?.data || res;
          if (data) {
            setFormData({
              name: data.name || '',
              description: data.description || '',
              academic_year_id: data.academic_year_id || '',
              class_id: data.class_id || '',
              section_id: data.section_id || '',
              is_default: data.is_default ?? true,
              is_active: data.is_active ?? true,
            });
          }
        })
        .catch((err) => {
          console.error('Failed to load fee structure details:', err);
          toast.error('Failed to load fee structure details');
        })
        .finally(() => setIsLoadingDetails(false));
    }
  }, [schoolId, isEditMode, id]);

  // Update available sections when class_id or classes list changes
  useEffect(() => {
    if (formData.class_id && classes.length > 0) {
      const selectedClass = classes.find((c) => (c.id || c.class_id) === formData.class_id);
      const sections = selectedClass?.sections || [];
      setAvailableSections(sections);
    }
  }, [formData.class_id, classes]);

  // Handle Class change
  const handleClassChange = (classId) => {
    const selectedClass = classes.find((c) => (c.id || c.class_id) === classId);
    const sections = selectedClass?.sections || [];
    setAvailableSections(sections);

    setFormData((prev) => ({
      ...prev,
      class_id: classId,
      section_id: sections.length > 0 ? (sections[0].id || sections[0].section_id) : '',
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Fee Structure Name is required.');
      return;
    }
    if (!formData.academic_year_id) {
      toast.error('Academic Session is required.');
      return;
    }
    if (!formData.class_id) {
      toast.error('Class is required.');
      return;
    }
    if (!formData.section_id) {
      toast.error('Section is required.');
      return;
    }

    const payload = {
      academic_year_id: formData.academic_year_id,
      class_id: formData.class_id,
      section_id: formData.section_id,
      name: formData.name.trim(),
      description: formData.description.trim() || '',
      is_default: formData.is_default,
      is_active: formData.is_active,
    };

    if (isEditMode) {
      updateFeeStructure(
        { id, data: payload },
        {
          onSuccess: () => {
            toast.success('Fee Structure updated successfully!');
            navigate(PATHS.FEES_STRUCTURE);
          },
          onError: (err) => {
            toast.error(err.message || 'Failed to update fee structure');
          },
        }
      );
    } else {
      createFeeStructure(payload, {
        onSuccess: (res) => {
          const newStructureId = res?.id || res?.data?.id || res?.structure_id || res?.data?.structure_id;
          toast.success('Fee Structure created! Add components for this structure.');
          if (newStructureId) {
            navigate(PATHS.FEES_STRUCTURE_COMPONENTS.replace(':id', newStructureId));
          } else {
            navigate(PATHS.FEES_STRUCTURE);
          }
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to create fee structure');
        },
      });
    }
  };

  return (
    <Box sx={{ pb: 6, width: '100%' }}>
      <PageHeader
        title={isEditMode ? 'Edit Fee Structure' : 'Create Fee Structure'}
        subtitle={
          isEditMode
            ? 'Update fee structure details for a class and academic session.'
            : 'Define a new fee structure for a class and academic session.'
        }
        onBack={() => navigate(PATHS.FEES_STRUCTURE)}
      />

      <WidgetContainer sx={{ mt: 3, p: 3, width: '100%' }}>
        {isLoadingDetails ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Loading fee structure details...
          </Typography>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                label="Fee Structure Name"
                name="name"
                required
                fullWidth
                size="small"
                placeholder="e.g. Standard Class 10 Fee Structure"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' }, width: '100%' }}>
                <FormControl size="small" fullWidth required sx={{ flex: 1 }}>
                  <InputLabel>Academic Session</InputLabel>
                  <Select
                    value={formData.academic_year_id}
                    label="Academic Session"
                    onChange={(e) => handleInputChange('academic_year_id', e.target.value)}
                  >
                    {sessions.map((session) => (
                      <MenuItem key={session.id} value={session.id}>
                        {session.session_name || session.name || `${session.start_date || ''} - ${session.end_date || ''}`}
                        {session.is_current ? ' (Current)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' }, width: '100%' }}>
                <FormControl size="small" fullWidth required sx={{ flex: 1 }}>
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={formData.class_id}
                    label="Class"
                    onChange={(e) => handleClassChange(e.target.value)}
                  >
                    {classes.map((c) => (
                      <MenuItem key={c.id || c.class_id} value={c.id || c.class_id}>
                        {c.class_name || c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth required sx={{ flex: 1 }} disabled={!formData.class_id}>
                  <InputLabel>Section</InputLabel>
                  <Select
                    value={formData.section_id}
                    label="Section"
                    onChange={(e) => handleInputChange('section_id', e.target.value)}
                  >
                    {availableSections.map((sec) => (
                      <MenuItem key={sec.id || sec.section_id} value={sec.id || sec.section_id}>
                        {sec.section_name || sec.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Description (Optional)"
                name="description"
                multiline
                rows={3}
                fullWidth
                size="small"
                placeholder="Provide additional context or details about this fee structure..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                  Settings
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_default}
                        onChange={(e) => handleInputChange('is_default', e.target.checked)}
                      />
                    }
                    label="Is Default Structure"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                        color="success"
                      />
                    }
                    label="Is Active"
                  />
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
                <Button onClick={() => navigate(PATHS.FEES_STRUCTURE)} disabled={isPending}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={isPending}>
                  {isPending
                    ? 'Saving...'
                    : isEditMode
                    ? 'Save Changes'
                    : 'Create Fee Structure'}
                </Button>
              </Box>
            </Stack>
          </form>
        )}
      </WidgetContainer>
    </Box>
  );
}
